import React, { useMemo, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { useI18n } from '../../i18n';
import type {
  BindingClientOption,
  BindingSiteOption,
  SiteBindingOptions,
  SiteBindingSelection,
} from '../../services/staffService';

interface Props {
  options: SiteBindingOptions;
  value: SiteBindingSelection;
  onChange: (selection: SiteBindingSelection) => void;
  disabled?: boolean;
  /** Distinguishes the inputs when two pickers could be on the page. */
  idPrefix: string;
}

/** Past this many sites a search box earns its place. */
const SEARCH_THRESHOLD = 8;

interface ClientGroup {
  /** Null for the sites that belong to no client. */
  client: BindingClientOption | null;
  sites: BindingSiteOption[];
}

/**
 * Chooses what a guard is bound to. Ticking a client binds them to all of its
 * sites, including ones added later, so its sites show as covered and cannot
 * be unticked one by one; ticking sites binds them to exactly those.
 */
export default function SiteBindingPicker({ options, value, onChange, disabled = false, idPrefix }: Props) {
  const { t } = useI18n();
  const [query, setQuery] = useState('');

  const boundClients = useMemo(() => new Set(value.clientContractIds), [value.clientContractIds]);
  const boundSites = useMemo(() => new Set(value.siteIds), [value.siteIds]);

  const groups = useMemo<ClientGroup[]>(() => {
    const term = query.trim().toLowerCase();
    const matches = (name: string) => !term || name.toLowerCase().includes(term);

    const byClient: ClientGroup[] = options.clients
      .map((client) => {
        const sites = options.sites.filter((site) => site.clientContractId === client.id);
        // A client that matches shows all its sites; otherwise only the sites that match.
        return { client, sites: matches(client.name) ? sites : sites.filter((site) => matches(site.name)) };
      })
      .filter(({ client, sites }) => {
        // Nothing at an archived client is being rostered, so it is only
        // listed while the guard is still bound there and can be taken off.
        if (client.archived) {
          return boundClients.has(client.id) || sites.some((site) => boundSites.has(site.id));
        }
        return matches(client.name) || sites.length > 0;
      });

    const clientIds = new Set(options.clients.map((client) => client.id));
    const unattached = options.sites.filter((site) => (
      (!site.clientContractId || !clientIds.has(site.clientContractId)) && matches(site.name)
    ));

    return unattached.length > 0 ? [...byClient, { client: null, sites: unattached }] : byClient;
  }, [boundClients, boundSites, options, query]);

  const toggleClient = (client: BindingClientOption, on: boolean) => {
    const covered = new Set(options.sites.filter((site) => site.clientContractId === client.id).map((site) => site.id));
    onChange(on
      ? {
          clientContractIds: [...value.clientContractIds, client.id],
          // The client now covers them; keeping both would say the same thing twice.
          siteIds: value.siteIds.filter((id) => !covered.has(id)),
        }
      : { ...value, clientContractIds: value.clientContractIds.filter((id) => id !== client.id) });
  };

  const toggleSite = (site: BindingSiteOption, on: boolean) => {
    onChange({
      ...value,
      siteIds: on ? [...value.siteIds, site.id] : value.siteIds.filter((id) => id !== site.id),
    });
  };

  return (
    <div className="staff-sites">
      {options.sites.length > SEARCH_THRESHOLD && (
        <label className="staff-sites__search">
          <FiSearch aria-hidden="true" />
          <input
            type="search"
            className="form__input"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('Search sites or clients')}
            aria-label={t('Search sites or clients')}
          />
        </label>
      )}

      <div className="staff-sites__list">
        {groups.length === 0 && (
          <p className="staff-form__hint">
            {options.sites.length === 0 && options.clients.length === 0
              ? t('There are no clients or sites to choose yet.')
              : t('No sites match.')}
          </p>
        )}

        {groups.map(({ client, sites }) => {
          const clientBound = Boolean(client && boundClients.has(client.id));
          const headId = `${idPrefix}-client-${client?.id ?? 'none'}`;
          return (
            <fieldset key={client?.id ?? 'none'} className="staff-sites__client" disabled={disabled}>
              {client ? (
                <label className="staff-sites__head" htmlFor={headId}>
                  <input
                    id={headId}
                    type="checkbox"
                    checked={clientBound}
                    onChange={(event) => toggleClient(client, event.target.checked)}
                  />
                  <span className="staff-sites__text">
                    <span className="staff-sites__name">
                      {client.name}
                      {client.archived && <span className="staff-sites__tag">{t('Archived')}</span>}
                    </span>
                    <span className="staff-sites__detail">
                      {t('Every site of this client, including ones added later')}
                    </span>
                  </span>
                </label>
              ) : (
                <div className="staff-sites__head staff-sites__head--plain">
                  <span className="staff-sites__name">{t('No client')}</span>
                </div>
              )}

              {sites.length > 0 && (
                <ul className="staff-sites__sites">
                  {sites.map((site) => (
                    <li key={site.id}>
                      <label className="staff-sites__site">
                        <input
                          type="checkbox"
                          checked={clientBound || boundSites.has(site.id)}
                          disabled={clientBound}
                          onChange={(event) => toggleSite(site, event.target.checked)}
                        />
                        <span className="staff-sites__site-name">{site.name}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </fieldset>
          );
        })}
      </div>
    </div>
  );
}
